<template>
  <div class="doc-section">
    <div class="section-header">
      <span class="material-icons-sharp section-icon">production_quantity_limits</span>
      <h2>Approvisionnement</h2>
    </div>

    <p class="section-intro">
      Le module Approvisionnement gère l'importation de marchandises : contrats fournisseurs, factures,
      connaissements (BL), dépenses douanières et suivi des conteneurs.
      Il est organisé en <strong>4 onglets</strong>.
      <strong>Accès :</strong> Administrateur, Resp. opérations, Resp. financier.
    </p>

    <!-- ===== CONTRATS ===== -->
    <h3 class="sub-title">Onglet 1 : Contrats</h3>
    <p class="section-text">Un contrat regroupe les commandes passées auprès d'un fournisseur.</p>

    <div class="table-wrapper">
      <table>
        <thead>
          <tr><th>Champ</th><th>Type</th><th>Obligatoire</th><th>Ce qu'il faut saisir</th></tr>
        </thead>
        <tbody>
          <tr><td><strong>Numéro de contrat</strong></td><td>Texte</td><td><span class="required-badge badge-yes">Oui</span></td><td>Le numéro unique du contrat (référence interne ou fournisseur).</td></tr>
          <tr><td><strong>Date de mise en place</strong></td><td>Date</td><td><span class="required-badge badge-yes">Oui</span></td><td>Date de signature ou d'entrée en vigueur du contrat.</td></tr>
          <tr><td><strong>Fournisseur</strong></td><td>Liste déroulante</td><td><span class="required-badge badge-yes">Oui</span></td><td>Sélectionnez le fournisseur dans la liste (créé au préalable dans Paramétrage).</td></tr>
          <tr><td><strong>Observation</strong></td><td>Texte</td><td><span class="required-badge badge-no">Non</span></td><td>Remarques sur le contrat.</td></tr>
        </tbody>
      </table>
    </div>

    <h4 class="sub-title">Comment créer un contrat</h4>
    <div class="steps">
      <div class="step-item">
        <div class="step-num">1</div>
        <p>Ouvrez le module <strong>Approvisionnement</strong> depuis le menu latéral.</p>
      </div>
      <div class="step-item">
        <div class="step-num">2</div>
        <p>Vous êtes sur l'onglet <strong>Contrats</strong> par défaut. Cliquez sur <span class="btn-ref">Ajouter</span>.</p>
      </div>
      <div class="step-item">
        <div class="step-num">3</div>
        <p>Remplissez <em>Numéro de contrat</em>, <em>Date de mise en place</em>, sélectionnez le <em>Fournisseur</em>.</p>
      </div>
      <div class="step-item">
        <div class="step-num">4</div>
        <p>Cliquez sur <span class="btn-ref">Soumettre</span>.</p>
      </div>
    </div>

    <h4 class="sub-title">Ajouter des factures à un contrat</h4>
    <p class="section-text">
      Après création du contrat, cliquez sur la ligne du contrat pour l'ouvrir. Le formulaire « Factures » s'affiche avec le tableau des factures.
    </p>
    <div class="steps">
      <div class="step-item">
        <div class="step-num">1</div>
        <p>Cliquez sur <span class="btn-ref">Ajouter une facture</span>. Un sous-formulaire apparaît.</p>
      </div>
      <div class="step-item">
        <div class="step-num">2</div>
        <p>Remplissez : <em>Produit</em> (liste déroulante), <em>N° de facture</em>, <em>Quantité (tonne)</em>, <em>Quantité (sac/carton)</em>, <em>Type de devise</em>, <em>Montant en devise</em>, <em>Montant FCFA</em>, <em>Observation</em>.</p>
      </div>
      <div class="step-item">
        <div class="step-num">3</div>
        <p>Cliquez sur <span class="btn-ref">Soumettre</span> pour enregistrer la facture.</p>
      </div>
    </div>

    <div class="table-wrapper">
      <table>
        <thead>
          <tr><th>Champ facture</th><th>Type</th><th>Ce qu'il faut saisir</th></tr>
        </thead>
        <tbody>
          <tr><td><strong>Produit</strong></td><td>Liste déroulante</td><td>Le produit commandé (sélectionné parmi le catalogue).</td></tr>
          <tr><td><strong>N° de facture</strong></td><td>Texte</td><td>Référence de la facture fournisseur.</td></tr>
          <tr><td><strong>Quantité (tonne)</strong></td><td>Nombre</td><td>Quantité totale en tonnes.</td></tr>
          <tr><td><strong>Quantité (type dynamique)</strong></td><td>Nombre</td><td>Quantité en unités (sacs, cartons, etc.).</td></tr>
          <tr><td><strong>Type de devise</strong></td><td>Liste déroulante</td><td>Devise de la transaction : USD, EUR, etc.</td></tr>
          <tr><td><strong>Montant (devise)</strong></td><td>Nombre</td><td>Montant dans la devise choisie.</td></tr>
          <tr><td><strong>Montant (FCFA)</strong></td><td>Nombre</td><td>Montant converti en FCFA.</td></tr>
          <tr><td><strong>Observation</strong></td><td>Texte</td><td>Remarques sur la facture.</td></tr>
        </tbody>
      </table>
    </div>

    <!-- ===== BL ===== -->
    <h3 class="sub-title">Onglet 2 : Connaissements (BL)</h3>
    <p class="section-text">Le BL (Bill of Lading) est le document de transport maritime qui accompagne chaque expédition.</p>

    <div class="table-wrapper">
      <table>
        <thead>
          <tr><th>Champ</th><th>Type</th><th>Ce qu'il faut saisir</th></tr>
        </thead>
        <tbody>
          <tr><td><strong>Facture</strong></td><td>Liste déroulante</td><td>La facture associée au BL.</td></tr>
          <tr><td><strong>Numéro BL</strong></td><td>Texte</td><td>Numéro unique du connaissement.</td></tr>
          <tr><td><strong>Port de chargement</strong></td><td>Texte</td><td>Port d'origine (ex : Bangkok).</td></tr>
          <tr><td><strong>Navire</strong></td><td>Texte</td><td>Nom du navire transporteur.</td></tr>
          <tr><td><strong>Ligne</strong></td><td>Texte</td><td>Nom de la compagnie maritime.</td></tr>
          <tr><td><strong>Destination</strong></td><td>Texte</td><td>Port de destination (ex : Abidjan).</td></tr>
          <tr><td><strong>ETD</strong></td><td>Date</td><td>Date de départ estimée.</td></tr>
          <tr><td><strong>ETA</strong></td><td>Date</td><td>Date d'arrivée estimée.</td></tr>
          <tr><td><strong>Dernière position</strong></td><td>Texte</td><td>Dernière position connue du navire.</td></tr>
          <tr><td><strong>Transitaire</strong></td><td>Liste déroulante</td><td>Le transitaire chargé du dédouanement.</td></tr>
          <tr><td><strong>Observation</strong></td><td>Texte</td><td>Remarques.</td></tr>
        </tbody>
      </table>
    </div>

    <p class="section-text">
      <strong>Procédure :</strong> Onglet <strong>BL</strong> → <span class="btn-ref">Ajouter</span> → Remplissez tous les champs → Ajoutez les conteneurs (type + quantité) → <span class="btn-ref">Soumettre</span>.
    </p>

    <!-- ===== DÉPENSES ===== -->
    <h3 class="sub-title">Onglet 3 : Dépenses (Sortie en douane)</h3>
    <p class="section-text">
      Pour chaque BL, vous pouvez enregistrer les dépenses associées : frais de transit, taxes douanières et autres frais.
    </p>
    <div class="steps">
      <div class="step-item">
        <div class="step-num">1</div>
        <p>Cliquez sur un BL dans le tableau pour l'ouvrir. Le formulaire « Sortie en douane » affiche les totaux (Transit, Taxe, Autres).</p>
      </div>
      <div class="step-item">
        <div class="step-num">2</div>
        <p>Cliquez sur <span class="btn-ref">Ajouter une dépense</span>.</p>
      </div>
      <div class="step-item">
        <div class="step-num">3</div>
        <p>Sélectionnez le <em>Type</em> (Transit ou Taxe). Selon le type, sélectionnez la <em>Désignation</em> dans la liste. Pour les taxes, saisissez le <em>Nombre de jours</em>. Saisissez le <em>Montant</em>.</p>
      </div>
      <div class="step-item">
        <div class="step-num">4</div>
        <p>Cliquez sur <span class="btn-ref">Soumettre</span>. La dépense est ajoutée au BL et les totaux sont mis à jour.</p>
      </div>
    </div>

    <!-- ===== CONTENEURS ===== -->
    <h3 class="sub-title">Onglet 4 : Conteneurs</h3>
    <p class="section-text">
      Suivi des conteneurs : conteneurs en attente (en transit) et conteneurs envoyés au stock.
    </p>

    <h4 class="sub-title">Ajouter un conteneur</h4>
    <div class="steps">
      <div class="step-item">
        <div class="step-num">1</div>
        <p>Cliquez sur <span class="btn-ref">Ajouter</span> dans l'onglet Conteneurs.</p>
      </div>
      <div class="step-item">
        <div class="step-num">2</div>
        <p>Sélectionnez le <em>Numéro BL</em>, saisissez le <em>Numéro conteneur</em>, le <em>Type de conteneur</em>, la <em>Quantité (tonne)</em>, la <em>Quantité (unités)</em> et une <em>Observation</em>.</p>
      </div>
      <div class="step-item">
        <div class="step-num">3</div>
        <p>Cliquez sur <span class="btn-ref">Soumettre</span>.</p>
      </div>
    </div>

    <h4 class="sub-title">Envoyer un conteneur au stock</h4>
    <div class="steps">
      <div class="step-item">
        <div class="step-num">1</div>
        <p>Sélectionnez le(s) conteneur(s) dans la liste « En attente » en cochant les cases.</p>
      </div>
      <div class="step-item">
        <div class="step-num">2</div>
        <p>Cliquez sur le bouton d'envoi en stock. Le formulaire « Envoi dans stock » s'ouvre.</p>
      </div>
      <div class="step-item">
        <div class="step-num">3</div>
        <p>Sélectionnez le <em>Magasin</em> de destination et la <em>Date d'envoi</em>.</p>
      </div>
      <div class="step-item">
        <div class="step-num">4</div>
        <p>Cliquez sur <span class="btn-ref">Soumettre sans modifier</span>. Le conteneur passe de « En attente » à « Envoyé » et le stock est incrémenté.</p>
      </div>
    </div>

    <div class="info-card info">
      <div class="card-title">
        <span class="material-icons-sharp" style="font-size:16px;color:#5b9bd5">info</span>
        Bon à savoir
      </div>
      <p>
        Les conteneurs envoyés au stock arrivent en statut « En attente de réception » dans le module Stocks. Le magasinier devra confirmer la réception avec la quantité réelle (voir module Stocks).
      </p>
    </div>
  </div>
</template>
<script>
export default { name: "DocApprovisionnement" };
</script>
